import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

// Helper: insert HTML around cursor selection in a textarea
function insertAtCursor(el: HTMLTextAreaElement, before: string, after: string) {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const selected = el.value.substring(start, end);
  const newVal = el.value.substring(0, start) + before + selected + after + el.value.substring(end);
  return { value: newVal, cursor: start + before.length + selected.length + after.length };
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface LegalDoc {
  id: string;
  doc_type: string;
  version: number;
  title: string;
  content_html: string;
  change_summary: string | null;
  published_at: string;
  published_by: string | null;
  is_current: boolean;
}

const DOC_TYPES = [
  { key: 'privacy',      label: 'Política de Privacidade' },
  { key: 'terms',        label: 'Termos de Uso' },
  { key: 'cancellation', label: 'Cancelamento e Reembolso' },
] as const;

type DocKey = (typeof DOC_TYPES)[number]['key'];

// ─── PDF helper ──────────────────────────────────────────────────────────────

function openPrintWindow(title: string, version: number, publishedAt: string, html: string) {
  const date = new Date(publishedAt).toLocaleDateString('pt-BR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const win = window.open('', '_blank');
  if (!win) { alert('Permita pop-ups para baixar o PDF.'); return; }
  win.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${title}, v${version}</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; max-width: 820px; margin: 0 auto; padding: 2.5rem 2rem; color: #1a1a1a; font-size: 14px; line-height: 1.75; }
    h1 { font-size: 1.75rem; margin-bottom: .25rem; }
    h2 { font-size: 1.15rem; margin-top: 2rem; margin-bottom: .5rem; border-bottom: 1px solid #ddd; padding-bottom: .25rem; }
    h3 { font-size: 1rem; margin-top: 1.25rem; margin-bottom: .25rem; }
    p, li { margin-bottom: .5rem; }
    table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
    th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; font-size: 13px; }
    th { background: #f5f5f5; }
    .meta { color: #555; font-size: 13px; margin-bottom: 2rem; }
    .watermark { position: fixed; bottom: 1rem; right: 1.5rem; font-size: 11px; color: #aaa; }
    @media print { .watermark { position: fixed; } }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <p class="meta">Versão ${version}, publicada em ${date}</p>
  <hr style="margin-bottom:2rem;border:none;border-top:1px solid #ccc">
  ${html}
  <div class="watermark">Professya, ${title} v${version}</div>
</body>
</html>`);
  win.document.close();
  win.focus();
  // Give browser time to render before triggering print
  setTimeout(() => { win.print(); }, 600);
}

// ─── Editor panel for one document type ──────────────────────────────────────

function DocEditor({ docKey, label }: { docKey: DocKey; label: string }) {
  const { user } = useAuth();
  const [history,     setHistory]     = useState<LegalDoc[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [htmlDraft,   setHtmlDraft]   = useState('');
  const [summary,     setSummary]     = useState('');
  const [msg,         setMsg]         = useState<{ ok: boolean; text: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function applyFormat(before: string, after: string) {
    const ta = textareaRef.current;
    if (!ta) return;
    const { value, cursor } = insertAtCursor(ta, before, after);
    setHtmlDraft(value);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(cursor, cursor); }, 0);
  }

  const current = history.find(d => d.is_current) ?? null;
  const nextVersion = current ? current.version + 1 : 1;

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('legal_documents')
      .select('id, doc_type, version, title, content_html, change_summary, published_at, published_by, is_current')
      .eq('doc_type', docKey)
      .order('version', { ascending: false });
    setHistory(data ?? []);
    // Pre-fill editor with current version content
    const cur = (data ?? []).find(d => d.is_current);
    setHtmlDraft(cur?.content_html ?? '');
    setLoading(false);
  }

  async function publish() {
    if (!htmlDraft.trim()) { setMsg({ ok: false, text: 'O conteúdo não pode estar vazio.' }); return; }
    setSaving(true);
    setMsg(null);

    // Unset is_current on all existing versions for this doc_type
    const { error: unsetErr } = await supabase
      .from('legal_documents')
      .update({ is_current: false })
      .eq('doc_type', docKey);
    if (unsetErr) { setMsg({ ok: false, text: `Erro ao despriorizar versões anteriores: ${unsetErr.message}` }); setSaving(false); return; }

    // Insert new version
    const { error: insertErr } = await supabase
      .from('legal_documents')
      .insert({
        doc_type:      docKey,
        version:       nextVersion,
        title:         label,
        content_html:  htmlDraft,
        change_summary: summary.trim() || null,
        published_by:  user?.id ?? null,
        is_current:    true,
      });

    if (insertErr) {
      setMsg({ ok: false, text: `Erro ao publicar: ${insertErr.message}` });
    } else {
      setMsg({ ok: true, text: `Versão ${nextVersion} publicada com sucesso.` });
      setSummary('');
      await load();
    }
    setSaving(false);
  }

  async function restore(doc: LegalDoc) {
    if (!confirm(`Restaurar versão ${doc.version} e torná-la a atual?`)) return;
    setSaving(true);
    await supabase.from('legal_documents').update({ is_current: false }).eq('doc_type', docKey);
    // Insert a new version based on the restored content
    const { error } = await supabase.from('legal_documents').insert({
      doc_type:      docKey,
      version:       (history[0]?.version ?? 0) + 1,
      title:         label,
      content_html:  doc.content_html,
      change_summary: `Restaurada a partir da versão ${doc.version}`,
      published_by:  user?.id ?? null,
      is_current:    true,
    });
    if (error) setMsg({ ok: false, text: `Erro: ${error.message}` });
    else { setMsg({ ok: true, text: 'Versão restaurada.' }); await load(); }
    setSaving(false);
  }

  if (loading) return <p style={{ color: '#8A7F6E' }}>Carregando...</p>;

  return (
    <div>
      {/* Current version info */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        {current ? (
          <div style={{ background: '#1a2a1a', border: '1px solid #4ade8033', borderRadius: 8, padding: '8px 16px', fontSize: 13 }}>
            <span style={{ color: '#4ade80' }}>✓ Versão atual: </span>
            <strong style={{ color: '#F5F0E8' }}>v{current.version}</strong>
            <span style={{ color: '#8A7F6E', marginLeft: 8 }}>
              publicada em {new Date(current.published_at).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        ) : (
          <div style={{ background: '#2a1a1a', border: '1px solid #ef444433', borderRadius: 8, padding: '8px 16px', fontSize: 13, color: '#f59e0b' }}>
            Nenhuma versão publicada, o conteúdo estático do site está sendo exibido.
          </div>
        )}
        {current && (
          <button
            onClick={() => openPrintWindow(label, current.version, current.published_at, current.content_html)}
            style={BTN_OUTLINE}
          >
            ⬇ Baixar versão atual (PDF)
          </button>
        )}
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 760, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ background: '#1F4E5F', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ margin: 0, fontWeight: 700, color: '#fff', fontSize: 15 }}>
                Pré-visualização, {label} v{nextVersion}
              </p>
              <button onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '1.5rem 2rem', flex: 1 }}>
              <div
                style={{ fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.8, color: '#1a1a1a' }}
                dangerouslySetInnerHTML={{ __html: htmlDraft }}
              />
            </div>
            <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowPreview(false)} style={{ ...BTN_SM, fontSize: 13, padding: '8px 16px' }}>Fechar e continuar editando</button>
              <button
                onClick={async () => { setShowPreview(false); await publish(); }}
                disabled={saving}
                style={{ ...BTN_PRIMARY, opacity: saving ? 0.5 : 1 }}
              >
                {saving ? 'Publicando...' : `✓ Aprovar e Publicar versão ${nextVersion}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', color: '#8A7F6E', fontSize: 12, marginBottom: 6, fontWeight: 600 }}>
          CONTEÚDO HTML, VERSÃO {nextVersion}
        </label>
        {/* Formatting toolbar */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: 8, background: '#1a1a2a', padding: '6px 8px', borderRadius: '8px 8px 0 0', border: '1px solid #30363d', borderBottom: 'none' }}>
          {[
            { label: 'B',   title: 'Negrito',            tag: '<strong>', end: '</strong>', style: { fontWeight: 'bold' } },
            { label: 'I',   title: 'Itálico',            tag: '<em>', end: '</em>', style: { fontStyle: 'italic' } },
            { label: 'U',   title: 'Sublinhado',         tag: '<u>', end: '</u>', style: { textDecoration: 'underline' } },
            { label: 'H2',  title: 'Título de seção',    tag: '\n<h2>', end: '</h2>\n', style: {} },
            { label: 'H3',  title: 'Subtítulo',          tag: '\n<h3>', end: '</h3>\n', style: {} },
            { label: 'P',   title: 'Parágrafo',          tag: '\n<p>', end: '</p>\n', style: {} },
            { label: 'UL',  title: 'Lista',              tag: '\n<ul>\n  <li>', end: '</li>\n</ul>\n', style: {} },
            { label: 'LI',  title: 'Item de lista',      tag: '\n  <li>', end: '</li>', style: {} },
          ].map(btn => (
            <button key={btn.label} onClick={() => applyFormat(btn.tag, btn.end)} title={btn.title} style={{ ...btn.style, background: '#2a2a3a', border: '1px solid #3a3a4a', borderRadius: 4, color: '#F5F0E8', padding: '3px 8px', fontSize: 12, cursor: 'pointer', minWidth: 32 }}>
              {btn.label}
            </button>
          ))}
          <div style={{ width: '1px', background: '#3a3a4a', margin: '0 4px' }} />
          {/* Font size shortcuts */}
          {[
            { label: 'Sm', title: 'Texto pequeno', tag: '<span style="font-size:0.85em">', end: '</span>' },
            { label: 'Lg', title: 'Texto grande',  tag: '<span style="font-size:1.15em">', end: '</span>' },
          ].map(btn => (
            <button key={btn.label} onClick={() => applyFormat(btn.tag, btn.end)} title={btn.title} style={{ background: '#2a2a3a', border: '1px solid #3a3a4a', borderRadius: 4, color: '#F5F0E8', padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>
              {btn.label}
            </button>
          ))}
          <div style={{ width: '1px', background: '#3a3a4a', margin: '0 4px' }} />
          {/* Color shortcuts */}
          {[
            { label: '⬤', color: '#1F4E5F', title: 'Petrol (destaque)' },
            { label: '⬤', color: '#D4A017', title: 'Ouro (aviso)' },
            { label: '⬤', color: '#ef4444', title: 'Vermelho' },
          ].map(btn => (
            <button key={btn.color} onClick={() => applyFormat(`<span style="color:${btn.color}">`, '</span>')} title={btn.title} style={{ background: '#2a2a3a', border: '1px solid #3a3a4a', borderRadius: 4, color: btn.color, padding: '3px 7px', fontSize: 13, cursor: 'pointer' }}>
              {btn.label}
            </button>
          ))}
        </div>
        <textarea
          ref={textareaRef}
          value={htmlDraft}
          onChange={e => setHtmlDraft(e.target.value)}
          placeholder={`Cole ou escreva aqui o HTML completo da ${label}.\n\nEnquanto nenhuma versão for publicada, o conteúdo estático do código continuará sendo exibido no site.`}
          rows={22}
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#07090F', color: '#c9d1d9',
            border: '1px solid #30363d', borderRadius: '0 0 8px 8px',
            padding: '12px 14px', fontFamily: "'Fira Code', 'Courier New', monospace",
            fontSize: 12.5, lineHeight: 1.65, resize: 'vertical', outline: 'none',
          }}
        />
        <p style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 0' }}>
          HTML completo do documento. Use a barra de formatação acima ou escreva as tags diretamente.
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', color: '#8A7F6E', fontSize: 12, marginBottom: 6, fontWeight: 600 }}>
          RESUMO DAS ALTERAÇÕES (opcional)
        </label>
        <input
          value={summary}
          onChange={e => setSummary(e.target.value)}
          placeholder="Ex: Atualização das seções de IA e transferências internacionais"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#0F1220', color: '#F5F0E8',
            border: '1px solid #2a2a3a', borderRadius: 8,
            padding: '10px 14px', fontSize: 13,
          }}
        />
      </div>

      {msg && (
        <div style={{
          background: msg.ok ? '#1a2a1a' : '#2a1a1a',
          border: `1px solid ${msg.ok ? '#4ade8066' : '#ef444466'}`,
          borderRadius: 8, padding: '10px 14px', fontSize: 13,
          color: msg.ok ? '#4ade80' : '#ef4444', marginBottom: 16,
        }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={() => { if (htmlDraft.trim()) setShowPreview(true); }}
          disabled={!htmlDraft.trim()}
          style={{ ...BTN_OUTLINE, opacity: !htmlDraft.trim() ? 0.5 : 1 }}
        >
          👁 Pré-visualizar antes de publicar
        </button>
        <button
          onClick={publish}
          disabled={saving || !htmlDraft.trim()}
          style={{ ...BTN_PRIMARY, opacity: saving || !htmlDraft.trim() ? 0.5 : 1 }}
        >
          {saving ? 'Publicando...' : `Publicar versão ${nextVersion} (sem preview)`}
        </button>
      </div>

      {/* Version history */}
      {history.length > 0 && (
        <div style={{ marginTop: 36 }}>
          <h3 style={{ color: '#D4A017', marginBottom: 12, fontSize: 15 }}>Histórico de versões</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1a1a2a' }}>
                <th style={TH}>Versão</th>
                <th style={TH}>Publicada em</th>
                <th style={TH}>Resumo</th>
                <th style={TH}>Status</th>
                <th style={TH}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {history.map(doc => (
                <tr key={doc.id} style={{ borderBottom: '1px solid #2a2a3a' }}>
                  <td style={{ ...TD, fontWeight: 700 }}>v{doc.version}</td>
                  <td style={TD}>{new Date(doc.published_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td style={{ ...TD, color: '#8A7F6E', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.change_summary ?? '-'}
                  </td>
                  <td style={TD}>
                    {doc.is_current
                      ? <span style={{ color: '#4ade80', fontWeight: 600 }}>● Atual</span>
                      : <span style={{ color: '#6b7280' }}>Anterior</span>}
                  </td>
                  <td style={{ ...TD, display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => openPrintWindow(label, doc.version, doc.published_at, doc.content_html)}
                      style={BTN_SM}
                      title="Baixar como PDF"
                    >
                      ⬇ PDF
                    </button>
                    <button
                      onClick={() => { setHtmlDraft(doc.content_html); setSummary(`Baseado na v${doc.version}`); textareaRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
                      style={BTN_SM}
                      title="Carregar este conteúdo no editor"
                    >
                      Editar
                    </button>
                    {!doc.is_current && (
                      <button
                        onClick={() => restore(doc)}
                        disabled={saving}
                        style={{ ...BTN_SM, color: '#f59e0b', borderColor: '#f59e0b55' }}
                        title="Restaurar esta versão como atual"
                      >
                        Restaurar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const TH: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', color: '#8A7F6E', fontWeight: 600, fontSize: 12 };
const TD: React.CSSProperties = { padding: '8px 12px', color: '#F5F0E8', verticalAlign: 'middle' };
const BTN_PRIMARY: React.CSSProperties = {
  background: '#D4A017', color: '#0A0D1A', border: 'none',
  borderRadius: 8, padding: '10px 24px', fontWeight: 700,
  fontSize: 14, cursor: 'pointer',
};
const BTN_OUTLINE: React.CSSProperties = {
  background: 'none', color: '#D4A017',
  border: '1px solid #D4A01766', borderRadius: 8,
  padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
};
const BTN_SM: React.CSSProperties = {
  background: '#1a1a2a', color: '#F5F0E8',
  border: '1px solid #3a3a4a', borderRadius: 6,
  padding: '4px 10px', fontSize: 12, cursor: 'pointer',
};

// ─── Main page ────────────────────────────────────────────────────────────────

export function AdminLegalDocsPage() {
  const [tab, setTab] = useState<DocKey>('privacy');
  const current = DOC_TYPES.find(d => d.key === tab)!;

  return (
    <div style={{ padding: '24px 32px', fontFamily: 'sans-serif', color: '#F5F0E8', maxWidth: 1000 }}>
      <AdminPageHeader
        title="Documentos Legais"
        objective="Edite e publique novas versões da Política de Privacidade, Termos de Uso e Política de Cancelamento. Cada publicação cria uma versão versionada e disponível para download em PDF."
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 28, borderBottom: '1px solid #2a2a3a' }}>
        {DOC_TYPES.map(d => (
          <button
            key={d.key}
            onClick={() => setTab(d.key)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '10px 18px', fontSize: 14, fontWeight: 600,
              color: tab === d.key ? '#D4A017' : '#8A7F6E',
              borderBottom: tab === d.key ? '2px solid #D4A017' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div style={{ background: '#0F1220', borderRadius: 12, padding: 28, border: '1px solid #2a2a3a' }}>
        <DocEditor key={tab} docKey={tab} label={current.label} />
      </div>
    </div>
  );
}
